import { Field } from "@sitecore-jss/sitecore-jss-react";
import moment from "moment";
import React, { useReducer } from "react";
import { useHistory } from "react-router-dom";

import { updateOtherData } from "../../../api/mutation";
import DatePicker from "../../../components/InputFields/DatePicker";
import { TextArea } from "../../../components/InputFields/TextArea";
import { V5, V10 } from "../../../components/InputFields/validator/validators";
import { ModelState } from "../../../interfaces/modelState";
import { force } from "../../../typeguards/force";
import { MutationLayout } from "../MutationLayout";
import { otherMutationSitecoreLabels } from "./OtherMutation.labels";
import { initialState, otherMutationReducer } from "./OtherMutation.state";

interface Props {
  fields: Record<string, Field<string> & { url: string }>;
  policyNr: string;
}

export const OtherMutation = ({ fields, policyNr }: Props) => {
  const history = useHistory();
  const minDate = moment().add(1, "days").toDate();
  const maxDate = moment().add(1, "days").add(1, "year").toDate();

  const [{ data, loading, modelState }, dispatch] = useReducer(otherMutationReducer, initialState);

  // create a copy of the object, because the fields try to mutate it
  const copyOfModelState = { ...modelState };

  const handleUpdateDescription = (description: string) => {
    dispatch({ type: "UPDATE_OTHER_MUTATION_DATA", payload: { description } });
  };

  const handleUpdateStartDate = (startDate?: string) => {
    dispatch({ type: "UPDATE_OTHER_MUTATION_DATA", payload: { startDate } });
  };

  const setModelState = (newModelState: ModelState) => {
    dispatch({ type: "UPDATE_OTHER_MUTATION_ERROR", payload: newModelState });
  };

  const handleBankAccountMutationClick = async () => {
    try {
      dispatch({ type: "UPDATE_OTHER_MUTATION_FETCH_START" });
      const request = {
        description: force(data.description),
        startDate: data.startDate,
      };
      const response = await updateOtherData({ ...request, policyNumber: policyNr });

      if (response.modelState) {
        dispatch({
          type: "UPDATE_OTHER_MUTATION_FETCH_ERROR",
          payload: response.modelState,
        });
      } else {
        // remove the query parameters
        window.history.replaceState(null, "", window.location.pathname);
        history.push(fields.ThankYouPage.url);
      }
    } catch {
      dispatch({
        type: "UPDATE_OTHER_MUTATION_FETCH_ERROR",
        payload: { global: ["Er is een onverwachte fout opgetreden. Probeer het opnieuw."] },
      });
    }
  };

  const canSubmitForm = Boolean(data.description);

  return (
    <MutationLayout
      canSubmitForm={canSubmitForm}
      handleSubmit={handleBankAccountMutationClick}
      loading={loading}
      modelState={copyOfModelState}
      title="Overige wijzigen"
      CardContent={
        <>
          <TextArea
            fieldData={otherMutationSitecoreLabels}
            name="Description"
            validators={[V5, V10(2000)]}
            setModelState={setModelState}
            inputValue={data.description}
            modelState={copyOfModelState}
            fieldName="Description"
            propertyName="model.Description"
            onBlur={handleUpdateDescription}
            required
          />
          <div className="form-row form-row--flex">
            <DatePicker
              required
              fieldData={otherMutationSitecoreLabels}
              name="StartDate"
              minDate={minDate}
              maxDate={maxDate}
              modelState={copyOfModelState}
              setModelState={setModelState}
              onChange={handleUpdateStartDate}
              inputValue={data.startDate}
              propertyName="model.StartDate"
            />
          </div>
        </>
      }
      InfoContent={
        <>
          <h6>Verwerkingstermijn</h6>
          <p>
            Wijzigingen zijn niet direct zichtbaar in het DELAportaal De verwerkingstermijn bedraagt maximaal 2
            werkdagen.
          </p>

          <h6>Wijzigen doorgeven</h6>
          <p>
            Een wijziging groter dan 2.000 karakters kun je hier niet doorgeven. Mail hiervoor naar{" "}
            <a href="mailto:relatiebeheer@dela.org">relatiebeheer@dela.org</a> o.v.v. naam verzekeringnemer en het
            polisnummer. Of bel naar 040 260 14 44.
          </p>
        </>
      }
    />
  );
};
